import { useState, useMemo } from "react";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { PRODUCTS, CATEGORIES, demoStats, fmt } from "../../data/store";

export default function Products() {
  const [list, setList] = useState(() => PRODUCTS.map((p) => ({ ...p })));
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const shown = useMemo(() => list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase())), [list, q]);

  const save = (e) => {
    e.preventDefault();
    setList((prev) => prev.map((p) => (p.id === edit.id ? edit : p)));
    setEdit(null);
    toast.success("Produkts saglabāts (DEMO — izmaiņas tikai šajā sesijā)");
  };
  const del = (id) => {
    setList((prev) => prev.filter((p) => p.id !== id));
    toast.success("Produkts dzēsts (DEMO)");
  };
  const addNew = () => {
    const id = Math.max(...list.map((p) => p.id)) + 1;
    const np = { ...list[0], id, sku: `DEMO-${String(id).padStart(4, "0")}`, name: "Jauns produkts", price: 1.0, oldPrice: null, isNew: true };
    setList((prev) => [np, ...prev]);
    setEdit(np);
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h1 className="text-2xl font-extrabold uppercase tracking-tight">Produkti ({shown.length})</h1>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Meklēt pēc nosaukuma vai SKU…" data-testid="products-search"
            className="text-sm border border-[#E2E2E2] px-3 py-2 w-64 bg-white focus:outline-none focus:ring-2 focus:ring-[#111111]" />
          <button onClick={addNew} data-testid="product-add-btn" className="bg-[#D0121A] hover:bg-[#A80D14] text-white text-sm font-bold px-4 py-2 flex items-center gap-1.5 transition-colors"><Plus size={15} /> Pievienot</button>
        </div>
      </div>
      <div className="bg-white border border-[#E2E2E2] overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#6B6B6B] border-b border-[#E2E2E2]">
            <th className="p-3">Prece</th><th>SKU</th><th>Kategorija</th><th className="text-right">Cena</th><th className="text-center">Noliktavā</th><th className="text-center">Pārdots/ned.</th><th></th>
          </tr></thead>
          <tbody>
            {shown.map((p) => {
              const s = demoStats(p);
              return (
                <tr key={p.id} className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5]" data-testid={`product-row-${p.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt="" className="w-10 h-10 object-cover bg-[#F5F5F5]" />
                      <div>
                        <div className="font-semibold line-clamp-1 max-w-[240px]">{p.name}</div>
                        <div className="text-xs text-[#6B6B6B]">{p.brand}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-[#6B6B6B]">{p.sku}</td>
                  <td className="text-[#6B6B6B]">{CATEGORIES.find((c) => c.slug === p.category)?.name.split(" ")[0]}</td>
                  <td className="text-right font-bold">{fmt(p.price)}</td>
                  <td className="text-center">
                    {p.stock ? <span className={`text-xs font-bold ${s.low ? "text-[#D0121A]" : "text-[#1E9E45]"}`}>{s.stockLevel} gab.</span> : <span className="text-xs text-[#E5A000] font-bold">Nav</span>}
                  </td>
                  <td className="text-center text-[#6B6B6B]">{s.soldWeek}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEdit({ ...p })} data-testid={`product-edit-${p.id}`} className="p-1.5 hover:bg-[#E2E2E2]" aria-label="Rediģēt"><Pencil size={14} /></button>
                      <button onClick={() => del(p.id)} data-testid={`product-delete-${p.id}`} className="p-1.5 hover:bg-[#E2E2E2] text-[#D0121A]" aria-label="Dzēst"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEdit(null)} />
          <form onSubmit={save} className="relative bg-white w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="product-edit-modal">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-extrabold uppercase">Produkta kartīte</h2>
              <button type="button" onClick={() => setEdit(null)} aria-label="Aizvērt"><X size={20} /></button>
            </div>
            <label className="block"><span className="text-sm font-semibold mb-1 block">Nosaukums</span>
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block"><span className="text-sm font-semibold mb-1 block">Cena € (ar PVN)</span>
                <input type="number" step="0.01" value={edit.price} onChange={(e) => setEdit({ ...edit, price: Number(e.target.value) })} data-testid="edit-price" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
              <label className="block"><span className="text-sm font-semibold mb-1 block">Vecā cena € (akcijai)</span>
                <input type="number" step="0.01" value={edit.oldPrice || ""} onChange={(e) => setEdit({ ...edit, oldPrice: e.target.value ? Number(e.target.value) : null })} data-testid="edit-old-price" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block"><span className="text-sm font-semibold mb-1 block">Iepakojumā (gab.)</span>
                <input type="number" value={edit.packQty} onChange={(e) => setEdit({ ...edit, packQty: Number(e.target.value) })} data-testid="edit-pack" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
              <label className="block"><span className="text-sm font-semibold mb-1 block">Zīmols</span>
                <input value={edit.brand} onChange={(e) => setEdit({ ...edit, brand: e.target.value })} data-testid="edit-brand" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
            </div>
            <label className="block"><span className="text-sm font-semibold mb-1 block">Kategorija</span>
              <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} data-testid="edit-category" className="w-full h-10 px-3 border border-[#E2E2E2] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#111111]">
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select></label>
            <label className="block"><span className="text-sm font-semibold mb-1 block">Apraksts</span>
              <textarea rows={3} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="edit-description" className="w-full px-3 py-2 border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" /></label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={edit.stock} onChange={(e) => setEdit({ ...edit, stock: e.target.checked })} data-testid="edit-stock" className="accent-[#D0121A]" /> Noliktavā</label>
              <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={edit.isNew} onChange={(e) => setEdit({ ...edit, isNew: e.target.checked })} data-testid="edit-isnew" className="accent-[#D0121A]" /> Jaunums</label>
            </div>
            <button type="submit" data-testid="product-save-btn" className="w-full bg-[#D0121A] hover:bg-[#A80D14] text-white font-bold py-3 transition-colors">Saglabāt</button>
          </form>
        </div>
      )}
    </div>
  );
}
